import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import DialogLayout from '../../../components/layout/DialogLayout';
import { useSnackbar } from 'notistack';
import { CircularProgress, Stack } from '@mui/material';
import { apiGetSharePriceHistory, apiDeleteSharePrice } from "../../../utils/api/api";
import ShareHistoryTable from './ShareHistoryTable';
import DeleteConfirmation from './DeleteConfirmation';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import SharePriceService from './SharePriceService';
/**
 * ShareUpdateHistory Component
 * 
 * Displays a dialog with the history of share price updates for a scheme.
 * Handles loading, displaying, and managing share price history records.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.toggleDialog - Function to toggle the dialog state
 * @param {Object} props.scheme - The selected scheme object
 * @param {Function} props.handleSave - Callback for when changes are saved (optional)
 * @param {Function} props.handleEdit - Callback for when a record is to be edited
 */
const ShareUpdateHistory = ({ open, toggleDialog, scheme, handleSave, handleEdit }) => {
    const [shareUpdateHistoryList, setShareUpdateHistoryList] = useState([]);
    const [editShareUpdateRid, setEditShareUpdateRid] = useState(null);
    const [openAlertDialog, setOpenAlertDialog] = useState(false);
    const [alertLoader, setAlertLoader] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    
    const { enqueueSnackbar } = useSnackbar();

    /**
     * Toggles the confirmation dialog for deletion
     */
    const toggleAlertDialog = () => {
        setOpenAlertDialog(!openAlertDialog);
    };

    /**
     * Handles the deletion of a share price update
     */
    const handleAlertDialog = () => {
        if (!editShareUpdateRid) {
            enqueueSnackbar("No record selected for deletion", { variant: "error" });
            return;
        }
        
        setAlertLoader(true);
        SharePriceService.deleteSharePrice(editShareUpdateRid)
            .then((data) => {
                setAlertLoader(false);
                enqueueSnackbar(data, { variant: "success" });
                toggleAlertDialog();
                getShareUpdateHistory();
            })
            .catch((error) => {
                setAlertLoader(false);
                enqueueSnackbar(error, { variant: "error" });
            });
    };

    /**
     * Fetches the share price history data from the API
     */
    const getShareUpdateHistory = useCallback(() => {
        if (!scheme?.oppRid) return;
        
        setIsLoading(true);
        SharePriceService.getSharePriceHistory(scheme.oppRid)
            .then((data) => {
                setIsLoading(false);
                setShareUpdateHistoryList(data);
                setHasLoaded(true);
            })
            .catch((error) => {
                setIsLoading(false);
                enqueueSnackbar(error, { variant: "error" });
                setShareUpdateHistoryList([]);
            });
    }, [scheme?.oppRid, enqueueSnackbar]);

    // Load data when the dialog opens with a scheme
    useEffect(() => {
        if (open && scheme?.oppRid && !hasLoaded) {
            getShareUpdateHistory();
        }
        if (!open) {
            setHasLoaded(false);
        }
    }, [open, scheme?.oppRid, hasLoaded, getShareUpdateHistory]);

    /**
     * Handles the deletion of a share price update
     * @param {string} id - The ID of the share price update to delete
     */
    const handleShareUpdateDelete = (id) => {
        setEditShareUpdateRid(id);
        toggleAlertDialog();
    };

    /**
     * Renders the appropriate content based on loading and data state
     */
    const renderContent = () => {
        if (isLoading) {
            return <LoadingState />;
        }
        
        return (
            <Stack direction="column" spacing={2} className='py-2'>
                {shareUpdateHistoryList.length > 0 ? (
                    <ShareHistoryTable 
                        shareUpdateHistoryList={shareUpdateHistoryList}
                        onDelete={handleShareUpdateDelete}
                        onEdit={handleEdit}
                    />
                ) : (
                    <EmptyState message="No share price history available" />
                )}
            </Stack>
        );
    };

    return (
        <DialogLayout
            open={open}
            title="Share Price History"
            subtitle={scheme?.oppTitle}
            maxWidth="md"
            closeAction={toggleDialog}
            positiveAction={{ label: 'Close', onClick: toggleDialog }}
        >
            <DeleteConfirmation
                open={openAlertDialog}
                isLoading={alertLoader}
                onCancel={toggleAlertDialog}
                onConfirm={handleAlertDialog}
            />
            {renderContent()}
        </DialogLayout>
    );
};

ShareUpdateHistory.propTypes = {
    open: PropTypes.bool.isRequired,
    toggleDialog: PropTypes.func.isRequired,
    scheme: PropTypes.object,
    handleSave: PropTypes.func,
    handleEdit: PropTypes.func
};

export default ShareUpdateHistory;